import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Flex,
  FlexProps,
  List,
  Menu,
  Modal,
  Paper,
  PaperProps,
  Space,
  Text
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBrandInstagram,
  IconCheck,
  IconCircleNumber1Filled,
  IconCircleNumber2Filled,
  IconCircleNumber3Filled,
  IconCircleNumber4Filled,
  IconCircleNumber5Filled,
  IconDotsVertical,
  IconExternalLink,
  IconTrash,
  IconX
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { DeauthorizedBadge } from 'components/DeauthorizedBadge';
import { SmartAssistantConnectedBadge } from 'components/SmartAssistantConnectedBadge';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_BY_INSTAGRAM_ACCOUNT_ID_QUERY_KEY } from 'shared/constants/query-keys';
import { InstagramAccount, InstagramAccountPermission } from 'shared/interfaces/InstagramAccount';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';

interface InstagramAccountProps extends PaperProps {
  account: InstagramAccount;
}

export function InstagramAccountCard({ account, ...paperProps }: InstagramAccountProps) {
  const { t } = useTranslation();

  const [removeAccountOpened, setRemoveAccountOpened] = useState(false);

  const accountUrl = `https://www.instagram.com/${account.username}`;
  const accountAccessSettingsUrl = `https://www.instagram.com/accounts/manage_access/`;

  const connectedSmartAssistantQuery = useQuery({
    queryKey: [SMART_ASSISTANT_BY_INSTAGRAM_ACCOUNT_ID_QUERY_KEY, account._id],
    queryFn: () => SmartAssistantService.getSmartAssistantsByInstagramAccountId(account._id)
  });

  const PermissionBadges = ({
    permissions,
    ...flexProps
  }: { permissions: InstagramAccountPermission[] } & FlexProps) => {
    const checkIcon = <IconCheck style={{ width: 12, height: 12, marginTop: 2 }} />;
    const xIcon = <IconX style={{ width: 12, height: 12, marginTop: 2 }} />;

    // Add more permissions here (hasCommentingPermission, hasAdsPermission, etc.)
    const hasMessagingPermission = [
      InstagramAccountPermission.INSTAGRAM_BUSINESS_BASIC,
      InstagramAccountPermission.INSTAGRAM_BUSINESS_MANAGE_MESSAGES
    ].every((perm) => permissions.includes(perm));

    const badges = [
      <Badge
        key="messaging"
        size="xs"
        color={hasMessagingPermission ? 'blue' : 'gray'}
        leftSection={hasMessagingPermission ? checkIcon : xIcon}>
        {t('INSTAGRAM.PERMISSIONS.MESSAGING')}
      </Badge>
    ];

    return (
      <Flex direction="row" align="center" gap={5} {...flexProps}>
        {badges.map((badge) => badge)}
      </Flex>
    );
  };

  return (
    <>
      <Paper p={5} px={10} withBorder {...paperProps}>
        <Flex direction="row" justify="space-between" align="center" w="100%" h="100%">
          <Flex direction="row" align="center">
            <IconBrandInstagram size={22} />
            <Space w={5} />
            <Text>{account.username}</Text>
            <Space w={10} />
            {account.accessRevokedDate ? <DeauthorizedBadge /> : <PermissionBadges permissions={account.permissions} />}
          </Flex>

          <Flex direction="row" align="center">
            {connectedSmartAssistantQuery.isFetched && !connectedSmartAssistantQuery.isError && (
              <>
                <SmartAssistantConnectedBadge
                  smartAssistant={connectedSmartAssistantQuery.data}
                  type={SmartAssistantType.INSTAGRAM}
                />
                <Space w={15} />
              </>
            )}
            <Menu position="right-start" withArrow arrowPosition="center" offset={0}>
              <Menu.Target>
                <ActionIcon onClick={(event) => event.stopPropagation()}>
                  <IconDotsVertical />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item icon={<IconExternalLink />} onClick={() => window.open(accountUrl, '_blank')}>
                  {t('INSTAGRAM.OPEN_ACCOUNT')}
                </Menu.Item>
                <Menu.Item icon={<IconTrash />} color="red" onClick={() => setRemoveAccountOpened(true)}>
                  {t('APP.ACTIONS.REMOVE')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </Paper>
      <Modal
        opened={removeAccountOpened}
        onClose={() => setRemoveAccountOpened(false)}
        title={t('INSTAGRAM.REMOVE.TITLE')}
        size="lg"
        centered>
        {connectedSmartAssistantQuery.data && (
          <Alert mb="xs" icon={<IconAlertCircle />} title={t('INSTAGRAM.REMOVE.WARNING.TITLE')} color="red">
            <Text fz="sm">{t('INSTAGRAM.REMOVE.WARNING.DESCRIPTION')}</Text>
          </Alert>
        )}
        <Paper bg="gray.1" p="md">
          <Text fz="sm">{t('INSTAGRAM.REMOVE.INFO')}</Text>
          <List size="sm" mt="xs" ml="md" type="ordered">
            <List.Item icon={<IconCircleNumber1Filled />}>{t('INSTAGRAM.REMOVE.STEP_1')}</List.Item>
            <List.Item icon={<IconCircleNumber2Filled />}>{t('INSTAGRAM.REMOVE.STEP_2')}</List.Item>
            <List.Item icon={<IconCircleNumber3Filled />}>{t('INSTAGRAM.REMOVE.STEP_3')}</List.Item>
            <List.Item icon={<IconCircleNumber4Filled />}>{t('INSTAGRAM.REMOVE.STEP_4')}</List.Item>
            <List.Item icon={<IconCircleNumber5Filled />}>{t('INSTAGRAM.REMOVE.STEP_5')}</List.Item>
          </List>
        </Paper>
        <Flex justify="flex-end">
          <Button
            mt="md"
            variant="outline"
            leftIcon={<IconExternalLink />}
            onClick={() => {
              window.open(accountAccessSettingsUrl, '_blank');
              setRemoveAccountOpened(false);
            }}>
            {t('INSTAGRAM.REMOVE.CTA')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
